import { Timestamp } from 'firebase/firestore';
import { HOUR24_DATE_CONFIG } from '../global';
import { ProviderNotifiedAssessment } from './assessments/provider-notified.model';
import { PsychosocialAssessment } from './assessments/psychosocial.model';
import { IntakeOutputAssessment } from './assessments/intake-output.model';
import { PainAssessment } from './assessments/pain.model';
import { IntegumentaryAssessment } from './assessments/integumentary.model';
import { GenitourinaryAssessment } from './assessments/genitourinary.model';
import { GastrointestinalAssessment } from './assessments/gastrointestinal.model';
import { MusculoskeletalAssessment } from './assessments/musculoskeletal.model';
import { NeurologicalAssessment } from './assessments/neurological.model';
import { RespiratoryAssessment } from './assessments/respiratory.model';
import { CardiovascularAssessment } from './assessments/cardiovascular.model';
import { HEENTAssessment } from './assessments/HEENT.model';
import { GeneralAppearanceAssessment } from './assessments/general-appearance.model';

/**
 * Holds information about the patient's nursing assessment.
 *
 * In the sub-assessment categories (cardiovascular, respiratory, etc.), there may be fields with multiple options available.
 * Some of these options are mutually exclusive; some can have multiple options selected. In the sub-assessment classes,
 * multiple-select fields are usually string[] types, and use the word "and" when listing the options in the documentation.
 * Single-select fields are usually non-list types, and use the word "or" when listing the options in the documentation.
 * The other types that use the word "or" are boolean fields: these fields are also "single-select", but only have two options,
 * hence the boolean variable type.
 */
export class Assessment {
  /**
   * The date and time the assessment was created.
   */
  date: Timestamp;

  /**
   * The general appearance assessment information.
   */
  generalAppearance: GeneralAppearanceAssessment;

  /**
   * The HEENNT assessment information.
   */
  heent: HEENTAssessment;

  /**
   * The cardiovascular assessment information.
   */
  cardiovascular: CardiovascularAssessment;

  /**
   * The respiratory assessment information.
   */
  respiratory: RespiratoryAssessment;

  /**
   * The neurological assessment information.
   */
  neurological: NeurologicalAssessment;
  /**
   * The musculoskeletal assessment information.
   */
  musculoskeletal: MusculoskeletalAssessment;

  /**
   * The gastrointestinal assessment information.
   */
  gastrointestinal: GastrointestinalAssessment;

  /**
   * The genitourinary assessment information.
   */
  genitourinary: GenitourinaryAssessment;

  /**
   * The psychosocial assessment information.
   */
  psychosocial: PsychosocialAssessment;

  /**
   * The integumentary assessment information.
   */
  integumentary: IntegumentaryAssessment;

  /**
   * The pain assessment information.
   */
  painAssessment: PainAssessment;

  /**
   * The intake and output information.
   */
  intakeOutput: IntakeOutputAssessment;

  /**
   * Provider Notified information
   */
  providerNotified: ProviderNotifiedAssessment;

  constructor(
    date?: Timestamp,
    generalAppearance?: GeneralAppearanceAssessment,
    heent?: HEENTAssessment,
    cardiovascular?: CardiovascularAssessment,
    respiratory?: RespiratoryAssessment,
    neurological?: NeurologicalAssessment,
    musculoskeletal?: MusculoskeletalAssessment,
    gastrointestinal?: GastrointestinalAssessment,
    genitourinary?: GenitourinaryAssessment,
    psychosocial?: PsychosocialAssessment,
    integumentary?: IntegumentaryAssessment,
    painAssessment?: PainAssessment,
    intakeOutput?: IntakeOutputAssessment,
    providerNotified?: ProviderNotifiedAssessment,
  ) {
    this.date = date ?? Timestamp.now();
    this.generalAppearance = generalAppearance ?? new GeneralAppearanceAssessment({});
    this.heent = heent ?? new HEENTAssessment({});
    this.cardiovascular = cardiovascular ?? new CardiovascularAssessment({});
    this.respiratory = respiratory ?? new RespiratoryAssessment({});
    this.neurological = neurological ?? new NeurologicalAssessment({});
    this.musculoskeletal = musculoskeletal ?? new MusculoskeletalAssessment({});
    this.gastrointestinal = gastrointestinal ?? new GastrointestinalAssessment({});
    this.genitourinary = genitourinary ?? new GenitourinaryAssessment({});
    this.psychosocial = psychosocial ?? new PsychosocialAssessment({});
    this.integumentary = integumentary ?? new IntegumentaryAssessment({});
    this.painAssessment = painAssessment ?? new PainAssessment({});
    this.intakeOutput = intakeOutput ?? new IntakeOutputAssessment({});
    this.providerNotified = providerNotified ?? new ProviderNotifiedAssessment({});
  }

  /**
   *
   * @returns The assessment's Timestamp as a date string, formatted as "MM/DD/YYYY @ HH:MM"
   */
  get dateString(): string {
    return this.date.toDate().toLocaleString([], HOUR24_DATE_CONFIG).replace(',', ' @');
  }
}
